import { useState } from "react";

/**
 * A field that turns typed or pasted Japanese into a log entry.
 *
 * Two places need it and they look nothing alike: the phone's compose row (`6c`), which is always
 * there because there is no tray agent to capture with, and the desktop chip at the foot of the
 * log, which you click open when you have text but no capture. Only the chrome differs, so only
 * the chrome lives in the callers.
 *
 * Submitting clears the field and leaves it open — a session usually arrives as several lines
 * pasted in a row, and closing after each one would make the second paste a click again.
 */
export function SentenceInput({
  onSubmit,
  placeholder,
  className,
  autoFocus,
  onEscape,
  onBlur,
  inputRef,
  enterKeyHint,
  ariaLabel = "読書ログに文を追加",
}: {
  onSubmit: (text: string) => void;
  placeholder: string;
  className: string;
  autoFocus?: boolean;
  onEscape?: () => void;
  /** Given the current text, so a caller can close only when nothing would be thrown away. */
  onBlur?: (text: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  enterKeyHint?: "go";
  ariaLabel?: string;
}) {
  const [text, setText] = useState("");

  return (
    <input
      ref={inputRef}
      autoFocus={autoFocus}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onBlur?.(text)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          if (!text.trim()) return;
          onSubmit(text);
          setText("");
        } else if (e.key === "Escape" && onEscape) {
          // preventDefault marks the key answered, so the window-level handlers — AddedPanel's
          // save-and-collapse in particular — leave this field's Escape alone.
          e.preventDefault();
          onEscape();
        }
      }}
      placeholder={placeholder}
      aria-label={ariaLabel}
      enterKeyHint={enterKeyHint}
      className={className}
    />
  );
}
